/**
 * Budget Performance Dashboard
 * Uses REAL data from training budget API
 */

import React, { useMemo, useState } from 'react'
import { DollarSign, TrendingUp, Award, Activity, Calendar } from 'lucide-react'
import {
  AnalyticsPerformanceDashboard,
  type MetricDefinition,
  type TimeSeriesDataPoint,
  type DetailView
} from '../AnalyticsPerformanceDashboard'
import type { ColumnDefinition } from '../AnalyticsDetailTable'
import type { FilterDimension } from '../AnalyticsFilters'
import { useTrainingBudget } from '@/hooks/useBudgets'
import { useTenant } from '@/hooks/useTenant'

/**
 * Format currency in Brazilian Real
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(value)
}

/**
 * Format percentage
 */
function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

/**
 * Format number with thousands separator
 */
function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value)
}

export function BudgetPerformanceDashboard() {
  const { tenant } = useTenant()
  const currentYear = new Date().getFullYear()

  // Filter state
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({})

  const { data: budget, isLoading, error } = useTrainingBudget(
    tenant?.domain || 'monumenta.localhost',
    currentYear
  )

  const metrics: MetricDefinition[] = useMemo(() => {
    if (!budget?.budget) return []

    const used = budget.actualCost || 0
    const total = budget.budget
    const available = total - used
    const utilizationRate = total > 0 ? (used / total) * 100 : 0

    return [
      {
        key: 'totalBudget',
        label: 'Orçamento Total',
        value: `R$ ${(total / 1000).toFixed(0)}K`,
        icon: DollarSign,
        color: 'brand-primary'
      },
      {
        key: 'used',
        label: 'Utilizado',
        value: `R$ ${(used / 1000).toFixed(0)}K`,
        change: `${utilizationRate.toFixed(1)}%`,
        icon: TrendingUp,
        color: 'warning'
      },
      {
        key: 'available',
        label: 'Disponível',
        value: `R$ ${(available / 1000).toFixed(0)}K`,
        change: `${((available / total) * 100).toFixed(1)}%`,
        icon: Award,
        color: 'success'
      },
      {
        key: 'utilizationRate',
        label: 'Taxa de Utilização',
        value: `${utilizationRate.toFixed(1)}%`,
        icon: Activity,
        color: 'brand-blue'
      }
    ]
  }, [budget])

  // Generate monthly budget consumption trend (simulated from total)
  const timeSeriesData: TimeSeriesDataPoint[] = useMemo(() => {
    if (!budget?.budget) return []

    const total = budget.budget
    const used = budget.actualCost || 0
    const currentMonth = new Date().getMonth()
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

    // Generate cumulative spending over months (up to current month)
    return monthNames.slice(0, currentMonth + 1).map((month, index) => {
      const progress = (index + 1) / (currentMonth + 1)
      const cumulativeUsed = used * progress
      const cumulativeAvailable = total - cumulativeUsed
      const rate = (cumulativeUsed / total) * 100

      return {
        date: month,
        totalBudget: total,
        used: cumulativeUsed,
        available: cumulativeAvailable,
        utilizationRate: rate
      }
    })
  }, [budget])

  // Detail views for by-month breakdown
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

  const byMonth = useMemo(() => {
    return timeSeriesData.map((item, index) => ({
      month: item.date,
      monthNumber: index + 1,
      totalBudget: item.totalBudget,
      used: item.used,
      available: item.available,
      utilizationRate: item.utilizationRate
    }))
  }, [timeSeriesData])

  const monthColumns: ColumnDefinition[] = [
    {
      key: 'month',
      label: 'Mês',
      sortable: false,
      align: 'left'
    },
    {
      key: 'totalBudget',
      label: 'Orçamento Total',
      sortable: true,
      align: 'right',
      render: (value: number) => formatCurrency(value),
      className: 'text-brand-gray'
    },
    {
      key: 'used',
      label: 'Utilizado',
      sortable: true,
      align: 'right',
      render: (value: number) => (
        <span className="font-semibold text-white">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'available',
      label: 'Disponível',
      sortable: true,
      align: 'right',
      render: (value: number) => formatCurrency(value),
      className: 'text-success'
    },
    {
      key: 'utilizationRate',
      label: 'Taxa de Utilização',
      sortable: true,
      align: 'right',
      render: (value: number) => (
        <div className="flex items-center justify-end gap-2">
          <div className="w-20 bg-brand-dark rounded-full h-2">
            <div
              className="bg-brand-primary h-2 rounded-full"
              style={{ width: `${Math.min(100, value)}%` }}
            />
          </div>
          <span className="text-brand-gray w-12 text-right">{formatPercent(value)}</span>
        </div>
      )
    }
  ]

  // Detail Views
  const detailViews: DetailView[] = [
    {
      id: 'by-month',
      label: 'Por Mês',
      icon: Calendar,
      columns: monthColumns,
      data: byMonth,
      keyExtractor: (row) => row.monthNumber.toString(),
      defaultSortBy: 'monthNumber',
      defaultSortOrder: 'asc',
      emptyMessage: 'Nenhum dado mensal disponível'
    }
  ]

  // Handle filter changes
  const handleFilterChange = (filterKey: string, values: string[]) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterKey]: values
    }))
  }

  const handleClearFilters = () => {
    setActiveFilters({})
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-brand-gray">Carregando orçamento...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-error">Erro ao carregar orçamento: {error.message}</div>
      </div>
    )
  }

  if (!budget || metrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-brand-gray">Nenhum orçamento disponível</div>
      </div>
    )
  }

  return (
    <AnalyticsPerformanceDashboard
      metrics={metrics}
      timeSeriesData={timeSeriesData}
      title="Performance de Orçamento de Treinamento"
      dateRange={`Ano ${currentYear} - Dados em tempo real`}
      enableComparison={true}
      detailViews={detailViews}
      defaultDetailView="by-month"
      onFilterChange={handleFilterChange}
    />
  )
}
